import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Drawer from '@app/components/Drawer';
import { SaveIcon } from '@app/components/icons';
import { useSitesApi } from '@app/hooks/useApis';
import type {
  ConnectorCluster,
  DiscoveryApiClass,
  DiscoveryJob,
  DiscoveryJobCreate,
  DiscoveryJobUpdate,
  DiscoveryTriggerScopeschema,
  Site
} from '@app/types/sdk';
import {
  listFromInput,
  buildCronWithTimezone,
  parseCronWithTimezone,
  describeSchedule,
  normalizeDiscoverySiteType
} from './helpers';

const TIMEZONE_OPTIONS = [
  'UTC',
  'America/Los_Angeles',
  'America/New_York',
  'Europe/London',
  'Europe/Paris',
  'Asia/Singapore'
];

const DOW_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' }
];

type DrawerMode = 'create' | 'edit';

interface DiscoveryJobDrawerProps {
  open: boolean;
  mode?: DrawerMode;
  orgId: string | null;
  jobId?: string | null;
  initialSiteId?: string | null;
  lockSite?: boolean;
  discoveryApi: DiscoveryApiClass;
  sites: Site[];
  clusters: ConnectorCluster[];
  onClose: () => void;
  onCreated?: (job: DiscoveryJob) => void;
  onUpdated?: (job: DiscoveryJob) => void;
}

type ScheduleMode = 'manual' | 'recurring';

interface TriggerScopeOption {
  value: string;
  label: string;
}

const QUICK_SCHEDULES = [
  { label: 'Hourly :00', frequency: 'hourly' as const, minute: '00' },
  { label: 'Daily 02:00', frequency: 'daily' as const, time: '02:00' },
  { label: 'Weekly Mon 04:00', frequency: 'weekly' as const, time: '04:00', dayOfWeek: '1' }
];

const pad = (value: number) => value.toString().padStart(2, '0');

const DiscoveryJobDrawer = ({
  open,
  mode = 'create',
  orgId,
  jobId,
  initialSiteId = null,
  lockSite = false,
  discoveryApi,
  sites,
  clusters,
  onClose,
  onCreated,
  onUpdated
}: DiscoveryJobDrawerProps) => {
  const isEditMode = mode === 'edit';
  const [siteId, setSiteId] = useState('');
  const [seedInput, setSeedInput] = useState('');
  const [cidrInput, setCidrInput] = useState('');
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('manual');
  const [timezone, setTimezone] = useState('UTC');
  const [recurringFrequency, setRecurringFrequency] = useState<'hourly' | 'daily' | 'weekly'>('daily');
  const [recurringTime, setRecurringTime] = useState('02:00');
  const [recurringMinute, setRecurringMinute] = useState('00');
  const [recurringDayOfWeek, setRecurringDayOfWeek] = useState('1');
  const [triggerScope, setTriggerScope] = useState('');
  const [triggerScopeOptions, setTriggerScopeOptions] = useState<TriggerScopeOption[]>([]);
  const [status, setStatus] = useState<'active' | 'paused'>('active');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingJob, setLoadingJob] = useState(false);
  const [jobEtag, setJobEtag] = useState<string | null>(null);
  const [siteClusterId, setSiteClusterId] = useState('');
  const formRef = useRef<HTMLFormElement | null>(null);
  const sitesApi = useSitesApi();
  const seedCount = useMemo(() => (seedInput.trim().length ? listFromInput(seedInput).length : 0), [seedInput]);
  const cidrCount = useMemo(() => (cidrInput.trim().length ? listFromInput(cidrInput).length : 0), [cidrInput]);

  const buildCronFromWizard = () => {
    const [hour = '02', minute = '00'] = recurringTime.split(':');
    const safeHour = pad(Math.min(Math.max(parseInt(hour, 10) || 0, 0), 23));
    const safeMinute = pad(Math.min(Math.max(parseInt(minute, 10) || 0, 0), 59));
    if (recurringFrequency === 'hourly') {
      const minuteOnly = pad(Math.min(Math.max(parseInt(recurringMinute, 10) || 0, 0), 59));
      return `${minuteOnly} */1 * * *`;
    }
    if (recurringFrequency === 'weekly') {
      return `${safeMinute} ${safeHour} * * ${recurringDayOfWeek}`;
    }
    return `${safeMinute} ${safeHour} * * *`;
  };

  const hydrateWizardFromCron = (cron: string): boolean => {
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) {
      return false;
    }
    const [minutePart, hourPart, , , dowPart] = parts;
    const minuteValue = pad(Math.min(Math.max(parseInt(minutePart, 10) || 0, 0), 59));
    const hourValue = pad(Math.min(Math.max(parseInt(hourPart, 10) || 0, 0), 23));
    if (hourPart.startsWith('*/') || hourPart === '*' || hourPart === '*/1') {
      setRecurringFrequency('hourly');
      setRecurringMinute(minuteValue);
      setScheduleMode('recurring');
      return true;
    }
    if (dowPart === '*' || dowPart === '?') {
      setRecurringFrequency('daily');
      setRecurringTime(`${hourValue}:${minuteValue}`);
      setScheduleMode('recurring');
      return true;
    }
    setRecurringFrequency('weekly');
    setRecurringDayOfWeek(dowPart);
    setRecurringTime(`${hourValue}:${minuteValue}`);
    setScheduleMode('recurring');
    return true;
  };

  const resetForm = useCallback(() => {
    setSiteId('');
    setSeedInput('');
    setCidrInput('');
    setScheduleMode('manual');
    setTimezone('UTC');
    setRecurringFrequency('daily');
    setRecurringTime('02:00');
    setRecurringMinute('00');
    setRecurringDayOfWeek('1');
    setTriggerScope('');
    setTriggerScopeOptions([]);
    setStatus('active');
    setSubmitting(false);
    setError(null);
    setLoadError(null);
    setLoadingJob(false);
    setJobEtag(null);
    setSiteClusterId('');
  }, []);

  const populateFromJob = useCallback((job: DiscoveryJob) => {
    setSiteId(job.siteId ?? '');
    setSeedInput((job.seeds ?? []).join('\n'));
    setCidrInput((job.targetCidrs ?? []).join('\n'));
    const schedule = (job.schedule ?? {}) as any;
    setTriggerScope(schedule.triggerScope ?? '');
    if (schedule.cron) {
      const parsed = parseCronWithTimezone(schedule.cron);
      setTimezone(parsed.timezone ?? 'UTC');
      if (!hydrateWizardFromCron(parsed.cron ?? '')) {
        setScheduleMode('manual');
      }
    } else {
      setScheduleMode('manual');
      setTimezone('UTC');
      setRecurringFrequency('daily');
      setRecurringTime('02:00');
      setRecurringMinute('00');
      setRecurringDayOfWeek('1');
    }
    setStatus(job.status === 'paused' ? 'paused' : 'active');
  }, []);

  const reloadJob = useCallback(async () => {
    if (!orgId || !jobId) {
      setLoadError('Pick a discovery job to edit.');
      return;
    }
    setLoadingJob(true);
    setLoadError(null);
    try {
      const response = await discoveryApi.orgsOrgIdDiscoveryJobsJobIdGetRaw({ orgId, jobId });
      const job = await response.value();
      populateFromJob(job);
      setJobEtag(response.raw.headers.get('ETag'));
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Failed to load discovery job.';
      setLoadError(message);
    } finally {
      setLoadingJob(false);
    }
  }, [discoveryApi, jobId, orgId, populateFromJob]);

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    setError(null);
    if (mode === 'create') {
      const scopedSiteId = initialSiteId?.trim() ?? '';
      if (scopedSiteId) {
        setSiteId(scopedSiteId);
      } else if (sites.length > 0) {
        setSiteId((prev) => (prev ? prev : sites[0].id ?? ''));
      }
      return;
    }
    reloadJob();
  }, [initialSiteId, open, mode, reloadJob, resetForm, sites]);

  const selectedSite = useMemo(() => sites.find((site) => site.id === siteId), [siteId, sites]);

  const selectedSiteName = useMemo(() => {
    return selectedSite?.name ?? siteId;
  }, [selectedSite, siteId]);

  const discoverySiteType = useMemo(() => normalizeDiscoverySiteType(selectedSite?.siteType), [selectedSite?.siteType]);
  const requiresConnectorTargets = discoverySiteType === 'onPrem';
  const usesTriggerScope = discoverySiteType !== 'onPrem';
  // Azure still submits its sole AZURE_CLOUD value, but only AWS offers a user choice.
  const showsTriggerScope = discoverySiteType === 'awsCloud';

  useEffect(() => {
    let cancelled = false;
    const loadSiteCluster = async () => {
      if (!orgId || !siteId || !requiresConnectorTargets) {
        setSiteClusterId('');
        return;
      }
      try {
        const clusterList = await sitesApi.orgsOrgIdSitesSiteIdConnectorClustersGet({ orgId, siteId, limit: 2 });
        const clusterIds = (clusterList.items ?? [])
          .map((cluster: ConnectorCluster) => cluster.id)
          .filter((value: string | undefined): value is string => typeof value === 'string' && value.trim().length > 0);
        if (!cancelled) {
          setSiteClusterId(clusterIds[0] ?? '');
        }
      } catch {
        if (!cancelled) {
          setSiteClusterId('');
        }
      }
    };
    void loadSiteCluster();
    return () => {
      cancelled = true;
    };
  }, [orgId, requiresConnectorTargets, siteId, sitesApi]);

  useEffect(() => {
    if (!requiresConnectorTargets) {
      setSeedInput('');
      setCidrInput('');
    }
  }, [requiresConnectorTargets]);

  useEffect(() => {
    let cancelled = false;
    const loadScheduleCapabilities = async () => {
      if (!open || !orgId || !siteId) {
        setTriggerScopeOptions([]);
        return;
      }
      try {
        const payload = await discoveryApi.orgsOrgIdDiscoverySitesSiteIdScheduleCapabilitiesGet(
          { orgId, siteId },
          { cache: 'no-store' }
        );
        if (!cancelled) {
          setTriggerScopeOptions(
            (payload.triggerScopes ?? []).filter(
              (option: DiscoveryTriggerScopeschema): option is TriggerScopeOption =>
                typeof option?.value === 'string' &&
                option.value.trim().length > 0 &&
                typeof option?.label === 'string' &&
                option.label.trim().length > 0
            )
          );
        }
      } catch {
        if (!cancelled) {
          setTriggerScopeOptions([]);
        }
      }
    };
    void loadScheduleCapabilities();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, open, orgId, siteId]);

  const clusterId = siteClusterId.trim();

  const selectedClusterName = useMemo(() => {
    const match = clusters.find((cluster) => cluster.id === clusterId);
    return match?.name ?? clusterId;
  }, [clusterId, clusters]);
  useEffect(() => {
    if (triggerScopeOptions.length === 0) {
      if (triggerScope !== '') {
        setTriggerScope('');
      }
      return;
    }
    if (!triggerScopeOptions.some((option) => option.value === triggerScope)) {
      setTriggerScope(triggerScopeOptions[0]?.value ?? '');
    }
  }, [triggerScope, triggerScopeOptions]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!orgId) {
      setError('Select an organization to create jobs.');
      return;
    }
    if (!siteId) {
      setError('Pick a site to scope the job.');
      return;
    }
    if (requiresConnectorTargets && !clusterId) {
      setError('Assign a connector cluster to the site before creating discovery jobs.');
      return;
    }
    const seeds = requiresConnectorTargets ? listFromInput(seedInput) : [];
    const targetCidrs = requiresConnectorTargets ? listFromInput(cidrInput) : [];
    if (requiresConnectorTargets && seeds.length === 0 && targetCidrs.length === 0) {
      setError('Provide at least one seed or CIDR.');
      return;
    }
    if (usesTriggerScope && !triggerScope) {
      setError('Select a discovery scope.');
      return;
    }
    const schedule =
      scheduleMode === 'recurring'
        ? {
            mode: 'recurring',
            cron: buildCronWithTimezone(buildCronFromWizard(), timezone),
            ...(usesTriggerScope ? { triggerScope } : {})
          }
        : usesTriggerScope
          ? { mode: 'manual', triggerScope }
          : undefined;

    const payload: DiscoveryJobCreate = {
      siteId,
      seeds,
      targetCidrs,
      schedule,
      status
    } as DiscoveryJobCreate;

    if (isEditMode && loadingJob) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (isEditMode) {
        if (!jobId) {
          throw new Error('Missing job reference.');
        }
        if (!jobEtag) {
          throw new Error('Unable to resolve job version. Reload and try again.');
        }
        type DiscoveryJobUpdatePayload = DiscoveryJobUpdate;
        const updatePayload: DiscoveryJobUpdatePayload = {
          seeds,
          targetCidrs,
          schedule,
          status
        } as DiscoveryJobUpdate;
        const response = await discoveryApi.orgsOrgIdDiscoveryJobsJobIdPatchRaw({
          orgId,
          jobId,
          ifMatch: jobEtag,
          discoveryJobUpdate: updatePayload as DiscoveryJobUpdate
        });
        const updatedJob = await response.value();
        setJobEtag(response.raw.headers.get('ETag'));
        onUpdated?.(updatedJob);
      } else {
        const job = await discoveryApi.orgsOrgIdDiscoveryJobsPost({
          orgId,
          discoveryJobCreate: payload
        });
        onCreated?.(job);
      }
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Failed to save discovery job.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const drawerTitle = selectedSiteName || (isEditMode ? 'Discovery job' : 'New discovery job');
  const formLocked = submitting || (isEditMode && loadingJob);
  const effectiveCronPreview = scheduleMode === 'recurring' ? buildCronFromWizard() : null;
  const selectedTriggerScopeLabel = triggerScopeOptions.find((option) => option.value === triggerScope)?.label;
  const scheduleSummary =
    scheduleMode === 'recurring'
      ? describeSchedule(effectiveCronPreview ?? '0 2 * * *')
      : 'Manual run';
  const drawerActions = (
    <button
      type="button"
      className="drawer-icon-button"
      aria-label="Save job"
      title="Save job"
      onClick={() => formRef.current?.requestSubmit()}
      disabled={formLocked}
    >
      <SaveIcon size={18} />
    </button>
  );

  return (
    <Drawer
      open={open}
      title={drawerTitle}
      subtitle={selectedSiteName || 'Discovery'}
      actions={drawerActions}
      onClose={() => {
        if (!submitting) {
          onClose();
        }
      }}
    >
      <form className="discovery-form" onSubmit={handleSubmit} ref={formRef}>
        {loadError && (
          <div className="discovery-form__error">
            <p>{loadError}</p>
            {isEditMode && (
              <button type="button" className="ui-button ui-button--ghost" onClick={() => reloadJob()} disabled={loadingJob}>
                {loadingJob ? 'Retrying…' : 'Retry load'}
              </button>
            )}
          </div>
        )}
        {isEditMode && loadingJob && !loadError && <p className="discovery-form__help">Loading discovery job…</p>}
        {(siteId || isEditMode) && (
          <div className="record-summary">
            <div className="record-summary__grid">
              <div>
                <span>Site</span>
                <strong>{selectedSiteName || 'Select a site'}</strong>
              </div>
              {requiresConnectorTargets && (
                <div>
                  <span>Cluster</span>
                  <strong>{selectedClusterName || 'Select a cluster'}</strong>
                </div>
              )}
              <div>
                <span>Status</span>
                <strong>{status === 'active' ? 'Active' : 'Paused'}</strong>
              </div>
              <div>
                <span>Schedule</span>
                <strong>{scheduleSummary}</strong>
              </div>
              {showsTriggerScope && (
                <div>
                  <span>Scope</span>
                  <strong>{selectedTriggerScopeLabel ?? triggerScope}</strong>
                </div>
              )}
              {requiresConnectorTargets && (
                <>
                  <div>
                    <span>Seeds</span>
                    <strong>{seedCount}</strong>
                  </div>
                  <div>
                    <span>Target CIDRs</span>
                    <strong>{cidrCount}</strong>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        <fieldset disabled={formLocked}>
          <div className="discovery-form__group discovery-form__group--triple">
            <label>
              Site
              <select value={siteId} onChange={(event) => setSiteId(event.target.value)} required disabled={isEditMode || lockSite}>
                <option value="">Select site</option>
                {siteId && !sites.some((site) => site.id === siteId) ? <option value={siteId}>{siteId}</option> : null}
                {sites.map((site) => (
                  <option key={site.id} value={site.id ?? ''}>
                    {site.name ?? site.id}
                  </option>
                ))}
              </select>
            </label>
            {requiresConnectorTargets && (
              <label>
                Connector cluster
                <input value={selectedClusterName || 'No cluster assigned'} readOnly disabled />
              </label>
            )}
            <label>
              Status
              <select value={status} onChange={(event) => setStatus(event.target.value as 'active' | 'paused')}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </label>
          </div>

          {requiresConnectorTargets && (
            <>
              <label>
                Seed devices or hosts
                <textarea
                  value={seedInput}
                  placeholder="10.42.0.1&#10;10.42.0.2"
                  onChange={(event) => setSeedInput(event.target.value)}
                  rows={3}
                />
                <small className="discovery-form__help">One entry per line or comma separated.</small>
              </label>

              <label>
                CIDR ranges (optional)
                <textarea
                  value={cidrInput}
                  placeholder="10.42.0.0/24&#10;10.42.1.0/24"
                  onChange={(event) => setCidrInput(event.target.value)}
                  rows={3}
                />
                <small className="discovery-form__help">Used to fan-out if the seeds do not cover the entire site.</small>
              </label>
            </>
          )}

          <fieldset className="discovery-form__fieldset">
            <legend>Schedule</legend>
            <div className="discovery-schedule__mode">
              <button
                type="button"
                className={['ui-button ui-button--ghost', scheduleMode === 'manual' ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => setScheduleMode('manual')}
              >
                Manual
              </button>
              <button
                type="button"
                className={['ui-button ui-button--ghost', scheduleMode === 'recurring' ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => setScheduleMode('recurring')}
              >
                Recurring
              </button>
            </div>
            {showsTriggerScope && triggerScopeOptions.length > 0 && (
              <div className="discovery-schedule__body">
                <label>
                  Scope
                  <select value={triggerScope} onChange={(event) => setTriggerScope(event.target.value)} required>
                    {triggerScopeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            {scheduleMode === 'recurring' && (
              <div className="discovery-schedule__body">
                <div className="discovery-schedule__grid">
                  <label>
                    Frequency
                    <select value={recurringFrequency} onChange={(event) => setRecurringFrequency(event.target.value as typeof recurringFrequency)}>
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </label>
                  {recurringFrequency === 'hourly' ? (
                    <label>
                      Minute past the hour
                      <select value={recurringMinute} onChange={(event) => setRecurringMinute(event.target.value)}>
                        {Array.from({ length: 12 }).map((_, index) => {
                          const value = pad(index * 5);
                          return (
                            <option key={value} value={value}>
                              {value} min
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  ) : (
                    <label>
                      Time of day
                      <input type="time" value={recurringTime} onChange={(event) => setRecurringTime(event.target.value)} />
                    </label>
                  )}
                  {recurringFrequency === 'weekly' && (
                    <label>
                      Day of week
                      <select value={recurringDayOfWeek} onChange={(event) => setRecurringDayOfWeek(event.target.value)}>
                        {DOW_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label>
                    Timezone
                    <select value={timezone} onChange={(event) => setTimezone(event.target.value)}>
                      {TIMEZONE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="discovery-form__preset-row">
                  {QUICK_SCHEDULES.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      className="ui-button ui-button--ghost"
                      onClick={() => {
                        setScheduleMode('recurring');
                        setRecurringFrequency(preset.frequency);
                        if (preset.frequency === 'hourly') {
                          setRecurringMinute(preset.minute ?? '00');
                        } else {
                          setRecurringTime(preset.time ?? '02:00');
                        }
                        if (preset.frequency === 'weekly') {
                          setRecurringDayOfWeek(preset.dayOfWeek ?? '1');
                        }
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </fieldset>
        </fieldset>

        {error && <p className="discovery-form__error">{error}</p>}
      </form>
    </Drawer>
  );
};

export default DiscoveryJobDrawer;
